"use client";

import {
  createContext,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { NextRouter } from "next/router";
import { getSnapshot, subscribe, routerEvents } from "./router-store";

export interface RouterFunctions {
  push: (url: any, as?: any, options?: any) => Promise<boolean>;
  replace: (url: any, as?: any, options?: any) => Promise<boolean>;
  reload: () => void;
  prefetch: (url: any, as?: any, options?: any) => Promise<void>;
  back: () => void;
  events: {
    on: (event: string, handler: (...args: any[]) => void) => void;
    off: (event: string, handler: (...args: any[]) => void) => void;
  };
  staticRouter: {
    pathname: string;
    asPath: string;
    query: Record<string, string | string[] | undefined>;
    isReady: boolean;
  };
}

export const RouterFuncsCtx = createContext<RouterFunctions | null>(null);

const ParamsCtx = createContext<Record<string, string>>({});

/**
 * Lets an App Router dynamic page inject its route params (e.g. { id })
 * so the compat router can surface them inside `router.query`, matching the
 * Pages Router behaviour of merging path params + search params.
 */
export function CompatParamsProvider({
  params,
  children,
}: {
  params: Record<string, string>;
  children: ReactNode;
}) {
  return <ParamsCtx.Provider value={params}>{children}</ParamsCtx.Provider>;
}

/**
 * Returns a Pages-Router-shaped object backed by whichever router is mounted:
 *  - under the Pages Router -> PagesCompatRouterProvider supplies real funcs
 *    and pushes pathname/asPath/query/isReady into the shared store
 *  - under the App Router -> AppCompatRouterProvider supplies next/navigation
 *    funcs and a UrlBridge keeps the store in sync with the current URL
 */
export function useCompatRouter(): NextRouter {
  const funcs = useContext(RouterFuncsCtx);
  const params = useContext(ParamsCtx);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (!funcs) {
    throw new Error(
      "useCompatRouter must be used within a CompatRouterProvider",
    );
  }

  const staticRouter = funcs.staticRouter;
  const pathname = snapshot.pathname || staticRouter.pathname;
  const asPath = snapshot.asPath || staticRouter.asPath;
  const isReady = snapshot.isReady || staticRouter.isReady;
  const query = useMemo(
    () => ({ ...params, ...(isReady ? snapshot.query : staticRouter.query) }),
    [params, isReady, snapshot.query, staticRouter.query],
  );

  const router = useMemo(
    () =>
      ({
        route: pathname,
        pathname,
        query,
        asPath,
        basePath: "",
        isFallback: false,
        isLocaleDomain: false,
        isPreview: false,
        isReady,
        ...funcs,
      }) as unknown as NextRouter,
    [pathname, asPath, isReady, query, funcs],
  );

  return router;
}

export { routerEvents };
